import { SettingKey, TenantSettingAffiliateBilling } from "types/enums";

import { TenantSettingsFormData } from "./formSchema";

type NumberSettingKeys = "language_id" | "country_id";
type FileSettingKeys = "logo" | "favicon";
type StringSettingKeys = "webhook_eid" | "webhook_advertiser_id" | "webhook_advertiser_custom_id" | "affiliate_billing";
type BooleanSettingKeys = "webhook_custom_parameters";

export type TenantSettingsDefaults = Partial<
    Pick<TenantSettingsFormData, NumberSettingKeys | FileSettingKeys | StringSettingKeys | BooleanSettingKeys>
>;

// currency_id is intentionally NOT here — it has its own dedicated endpoint
// (POST /settings/currency, cooldown + report reconversion) and must never go through the
// generic settings save. See useGetWorkspaceCurrencyQuery/useUpdateWorkspaceCurrencyMutation.
const settingFields: Record<keyof TenantSettingsDefaults, SettingKey> = {
    language_id: SettingKey.Language,
    country_id: SettingKey.Country,
    logo: SettingKey.TenantLogo,
    favicon: SettingKey.TenantFavicon,
    webhook_custom_parameters: SettingKey.WebhookCustomParameters,
    webhook_eid: SettingKey.WebhookEid,
    webhook_advertiser_id: SettingKey.WebhookAdvertiserId,
    webhook_advertiser_custom_id: SettingKey.WebhookAdvertiserCustomId,
    affiliate_billing: SettingKey.AffiliateBilling,
};

const numberSettingKeys = new Set<keyof TenantSettingsDefaults>(["language_id", "country_id"]);
const booleanSettingKeys = new Set<keyof TenantSettingsDefaults>(["webhook_custom_parameters"]);
const fileSettingKeys = new Set<keyof TenantSettingsDefaults>(["logo", "favicon"]);

export function buildSettingsPayload(
    formData: TenantSettingsFormData,
    existingSettings: App.Data.SettingData[]
): Array<App.Http.Requests.Setting.UpdateSettingRequest> {
    const updated = (Object.entries(settingFields) as Array<[keyof TenantSettingsDefaults, SettingKey]>).reduce<
        Record<string, string | null | undefined>
    >((acc, [formKey, settingKey]) => {
        const value = formData[formKey];
        if (fileSettingKeys.has(formKey) && value === undefined) {
            return acc;
        }
        acc[settingKey] = value?.toString() ?? null;
        return acc;
    }, {});

    const withType = (key: string, value: string | null) =>
        fileSettingKeys.has(
            (Object.keys(settingFields) as Array<keyof TenantSettingsDefaults>).find(
                (formKey) => settingFields[formKey] === key
            )!
        )
            ? { key, value, type: "file" as const }
            : { key, value };

    return [
        ...existingSettings
            // currency is deliberately excluded here too, not just from `updated` above —
            // otherwise the existing currency row would still ride along unchanged as a
            // pass-through on every save, silently reaching the generic endpoint that now
            // rejects it outright (see StoreSettingRequest/UpdateSettingRequest on the BE).
            .filter((s) => s.key !== SettingKey.Currency)
            .filter((s) => {
                const formKey = (Object.keys(settingFields) as Array<keyof TenantSettingsDefaults>).find(
                    (key) => settingFields[key] === s.key
                );
                return !(formKey && fileSettingKeys.has(formKey) && updated[s.key] === undefined);
            })
            .map((s) =>
                updated[s.key] !== undefined
                    ? withType(s.key, updated[s.key] as string | null)
                    : { key: s.key, value: s.value }
            ),
        ...Object.entries(updated)
            .filter(([key]) => !existingSettings.some((s) => s.key === key))
            .map(([key, value]) => withType(key, value as string | null)),
    ];
}

export function getSettingsDefaultValues(settings: App.Data.SettingData[]): TenantSettingsDefaults {
    const defaults: TenantSettingsDefaults = {
        logo: null,
        favicon: null,
        webhook_custom_parameters: false,
        webhook_eid: "",
        webhook_advertiser_id: "",
        webhook_advertiser_custom_id: "",
        // Matches the BE default in BalanceService::resolveAffiliateBilling() when no setting
        // row exists yet for this tenant.
        affiliate_billing: TenantSettingAffiliateBilling.UninvoicedBalance,
    };

    settings.forEach((s) => {
        const formKey = (Object.keys(settingFields) as Array<keyof TenantSettingsDefaults>).find(
            (key) => settingFields[key] === s.key
        );

        if (!formKey) {
            return;
        }

        if (numberSettingKeys.has(formKey)) {
            defaults[formKey] = Number(s.value) as never;
            return;
        }

        if (booleanSettingKeys.has(formKey)) {
            defaults[formKey] = (s.value === "true") as never;
            return;
        }

        if (fileSettingKeys.has(formKey)) {
            defaults[formKey] = (s.media?.[0]?.url ?? s.value ?? null) as never;
            return;
        }

        defaults[formKey] = (s.value ?? "") as never;
    });

    return defaults;
}
